"use client";

import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = ["Trust Structuring", "Estate Planning", "Asset Protection", "Sovereign Citizenship"];
const budgetOptions = ["Under $50,000", "$50,000 – $150,000", "$150,000 – $500,000", "Over $500,000"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-[var(--primary)] bg-[var(--card)]/60 backdrop-blur-md">
        <div className="font-mono text-xs uppercase tracking-widest text-[var(--primary)] mb-4">
          [ INQUIRY / RECEIVED ]
        </div>
        <p className="font-serif text-xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          [ FORM / LEGAL CONSULTATION ]
        </span>
        <div className="flex items-center gap-2">
          <span className="size-2 bg-[var(--primary)] animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-widest text-[var(--primary)]">ACTIVE</span>
        </div>
      </div>

      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg font-default focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:shadow-[0_4px_20px_-8px_var(--primary)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg font-default focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:shadow-[0_4px_20px_-8px_var(--primary)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone_number" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg font-default focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:shadow-[0_4px_20px_-8px_var(--primary)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget_range" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Estimated Asset Value
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg font-default focus-visible:ring-0 focus-visible:border-[var(--primary)] w-full">
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent>
                  {budgetOptions.map((opt, i) => <SelectItem key={i} data-index={i} value={opt} className="font-mono text-sm uppercase tracking-wider">
                      {opt}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="services_interested" rules={{
      validate: (value: string[] | undefined) => value && value.length > 0 || "Select at least one service"
    }} render={({
      field
    }) => {
      const selected: string[] = field.value || [];
      const toggle = (service: string) => {
        if (selected.includes(service)) {
          field.onChange(selected.filter(s => s !== service));
        } else {
          field.onChange([...selected, service]);
        }
      };
      return <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Legal Focus
              </FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-3 pt-2">
                  {services.map((service, i) => {
              const isActive = selected.includes(service);
              return <Button key={i} data-index={i} type="button" onClick={() => toggle(service)} variant={isActive ? "default" : "outline"} className={`font-mono text-xs uppercase tracking-widest px-4 py-2 h-auto ${isActive ? "bg-[var(--primary)] text-[var(--primary-foreground)] border-[var(--primary)] shadow-[0_0_20px_-4px_var(--primary)]" : "bg-transparent text-foreground border-border"}`}>
                        {service}
                      </Button>;
            })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>;
    }} />

      <FormField control={control} name="project_details" rules={{
      required: "Details are required",
      minLength: {
        value: 20,
        message: "Min 20 characters"
      },
      maxLength: {
        value: 1500,
        message: "Max 1500 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Case Summary
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your legal objectives or jurisdictional concerns..." rows={6} {...field} className="bg-transparent border-0 border-b border-border px-0 text-lg font-default focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:shadow-[0_4px_20px_-8px_var(--primary)] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_privacy" rules={{
      validate: v => v === true || "You must agree to the privacy terms"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value || false} onCheckedChange={field.onChange} className="mt-1" />
              </FormControl>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground leading-relaxed">
                I acknowledge VESTA's attorney-client privilege and privacy policy.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">{submitError}</p>
        </div>}

      <div className="space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90 font-mono text-sm uppercase tracking-widest shadow-2xl">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Request Consultation"}
        </Button>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center">
          [ Response Time: 24 Business Hours ]
        </p>
      </div>
    </Form>;
}
export default function ConsultationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3552" id="consultation-form" className="dark bg-background text-foreground py-20 md:py-32 px-6 md:px-12 relative overflow-hidden">
      <div className="max-w-7xl mx-auto flex items-center justify-between mb-16 md:mb-24">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          VESTA / CONSULT
        </div>
        <div className="font-mono text-xs uppercase tracking-widest text-[var(--primary)]">
          [ JURISDICTION / 001 ]
        </div>
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="lg:col-span-5 space-y-12">
          <div className="font-mono text-xs uppercase tracking-widest text-[var(--primary)]">
            [ 01 / INQUIRY ]
          </div>

          <h2 className="font-default text-5xl md:text-6xl lg:text-7xl font-bold uppercase leading-[0.95] tracking-tighter">
            Secure Your
            <br />
            <span className="text-transparent" style={{
            WebkitTextStroke: "1px var(--foreground)"
          }}>
              Legacy.
            </span>
          </h2>

          <p className="font-default text-xl text-muted-foreground leading-relaxed max-w-md">
            Begin your consultation with VESTA. We provide meticulous legal analysis for trust structures and sovereign citizenship.
          </p>

          <div className="space-y-6 pt-4">
            <div className="space-y-1">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                [ Direct ]
              </div>
              <div className="font-mono text-sm text-foreground">counsel@vesta-law.md</div>
            </div>
            <div className="space-y-1">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                [ Private ]
              </div>
              <div className="font-mono text-sm text-foreground">
                secure@vesta-law.md / PGP available
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-border">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground leading-relaxed">
              All consultations are strictly confidential.
              <br />
              Attorney-client privilege applies.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <FormProvider formKey="consultation_form_contact" formId="consultation_form_contact" sectionName="consultation-form" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          services_interested: [],
          project_details: "",
          budget_range: "",
          agree_privacy: false
        }} submitText="Request Consultation" successMessage="Consultation request received. Our team will review your case and contact you within 24 hours." errorMessage="Submission failed. Please verify your details or contact our office directly." className="bg-[var(--card)]/40 backdrop-blur-xl border border-border p-8 md:p-16 shadow-2xl" style={{
          boxShadow: "0 60px 100px -24px hsla(0, 0%, 0%, 0.9)"
        }}>
            <InquiryForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}